// src/screens/NewsDetailScreen.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import api from '../api';

import NewsCommentComposer from '../components/news/NewsCommentComposer';
import NewsCommentItem from '../components/news/NewsCommentItem';

import backIcon from '../assets/backspace-icon.png';
import piNetworkLogo from '../assets/pinetworklogo-icon.jpg';
import newsGoldTickIcon from '../assets/newsgoldtick-icon.png';
import discussIcon from '../assets/discuss-icon.png';
import sendIcon from '../assets/articlesend-icon.png';
import placeholderProfile from '../assets/placeholder-profile.png';


import './NewsDetailScreen.css';

const COMMENT_PAGE_LIMIT = 20;

function formatPublishedDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(
      undefined,
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }
    ).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

function getCommentId(comment) {
  return String(
    comment?.id ||
    comment?._id ||
    ''
  );
}

function getDiscussionParticipant(item) {
  const author =
    item?.author &&
    typeof item.author === 'object'
      ? item.author
      : {};

  return {
    id:
      author?._id ||
      item?.authorId ||
      '',

    username:
      author?.username ||
      item?.username ||
      '',

    name:
      author?.name ||
      author?.username ||
      item?.username ||
      'BarterFi User',

    profilePic:
      author?.profilePic ||
      placeholderProfile,
  };
}

function getDiscussionParticipantKey(item) {
  const participant =
    getDiscussionParticipant(item);

  return String(
    participant.id ||
    participant.username ||
    ''
  ).toLowerCase();
}

function getDiscussionActivityTime(item) {
  const value =
    item?.createdAt ||
    item?.updatedAt ||
    0;

  const timestamp =
    new Date(value).getTime();

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
}


function markItemDeleted(item) {
  return {
    ...item,
    text: '',
    status: 'deleted',
    isDeleted: true,
    canDelete: false,
    deletedAt:
      item?.deletedAt ||
      new Date().toISOString(),
  };
}


function updateNewsCommentReactionState(
  comments,
  targetId,
  {
    reactionSummary = [],
    viewerReaction = null,
  } = {}
) {
  if (
    !Array.isArray(comments) ||
    !targetId
  ) {
    return comments;
  }

  return comments.map(
    (comment) => {
      const commentId =
        getCommentId(comment);

      /*
       * Target is a top-level comment.
       */
      if (
        commentId === targetId
      ) {
        return {
          ...comment,

          reactionSummary:
            Array.isArray(
              reactionSummary
            )
              ? reactionSummary
              : [],

          viewerReaction:
            viewerReaction ||
            null,
        };
      }

      /*
       * Otherwise check the flat replies array
       * belonging to this root discussion.
       */
      const replies =
        Array.isArray(
          comment?.replies
        )
          ? comment.replies
          : [];

      let replyChanged = false;

      const nextReplies =
        replies.map(
          (reply) => {
            if (
              getCommentId(
                reply
              ) !== targetId
            ) {
              return reply;
            }

            replyChanged = true;

            return {
              ...reply,

              reactionSummary:
                Array.isArray(
                  reactionSummary
                )
                  ? reactionSummary
                  : [],

              viewerReaction:
                viewerReaction ||
                null,
            };
          }
        );

      if (!replyChanged) {
        return comment;
      }

      return {
        ...comment,
        replies:
          nextReplies,
      };
    }
  );
}

function NewsDetailScreen({
  user,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    articleId,
  } = useParams();

  const routeArticle =
    location.state?.article ||
    null;

  const [
    article,
    setArticle,
  ] = useState(routeArticle);

  const [
    loading,
    setLoading,
  ] = useState(!routeArticle);

  const [
    error,
    setError,
  ] = useState('');

  const [
    imageFailed,
    setImageFailed,
  ] = useState(false);

  /* =========================================================
     COMMENTS STATE
  ========================================================= */

  const [
    comments,
    setComments,
  ] = useState([]);

  const [
    commentsLoading,
    setCommentsLoading,
  ] = useState(true);

  const [
    commentsError,
    setCommentsError,
  ] = useState('');

  const [
    loadingMoreComments,
    setLoadingMoreComments,
  ] = useState(false);

  const [
    commentsPagination,
    setCommentsPagination,
  ] = useState({
    page: 1,
    limit: COMMENT_PAGE_LIMIT,
    total: 0,
    totalPages: 1,
    hasMore: false,
  });

  /* =========================================================
     COMMENT MUTATION STATE
  ========================================================= */

  const [
    commentSubmitting,
    setCommentSubmitting,
  ] = useState(false);

  const [
    commentSubmitError,
    setCommentSubmitError,
  ] = useState('');

  const [
    replySubmittingId,
    setReplySubmittingId,
  ] = useState('');

  const [
    replyErrors,
    setReplyErrors,
  ] = useState({});


const [
  replyTarget,
  setReplyTarget,
] = useState(null);

  const [
    deletingId,
    setDeletingId,
  ] = useState('');

  const [
    deleteError,
    setDeleteError,
  ] = useState('');


const [
  reactingId,
  setReactingId,
] = useState('');


const commentComposerRef =
  useRef(null);

const keepReplyTargetVisible =
  useCallback((targetId) => {
    if (!targetId) {
      return;
    }

    const scrollArea =
      document.querySelector(
        '.news-detail-scroll-area'
      );

    const footer =
      document.querySelector(
        '.news-detail-comment-footer'
      );

    const header =
      document.querySelector(
        '.news-detail-header'
      );

    const target =
      Array.from(
        document.querySelectorAll(
          '[data-news-reply-target-id]'
        )
      ).find(
        (element) =>
          element.dataset
            .newsReplyTargetId ===
          targetId
      );

    if (
      !scrollArea ||
      !target
    ) {
      return;
    }

    const targetRect =
      target.getBoundingClientRect();

    const headerRect =
      header?.getBoundingClientRect();

    const footerRect =
      footer?.getBoundingClientRect();

    const viewportBottom =
      window.visualViewport
        ? window.visualViewport.offsetTop +
          window.visualViewport.height
        : window.innerHeight;

    const safeTop =
      (headerRect?.bottom || 0) + 14;

    const safeBottom =
      Math.min(
        footerRect?.top ||
          viewportBottom,
        viewportBottom
      ) - 14;

    if (
      targetRect.top >= safeTop &&
      targetRect.bottom <= safeBottom
    ) {
      return;
    }

    const availableHeight =
      Math.max(
        0,
        safeBottom - safeTop
      );

    let desiredTop;

    if (
      targetRect.height <
      availableHeight
    ) {
      desiredTop =
        safeTop +
        Math.max(
          0,
          (
            availableHeight -
            targetRect.height
          ) / 2
        );
    } else {
      desiredTop = safeTop;
    }

    const scrollDifference =
      targetRect.top -
      desiredTop;

    scrollArea.scrollBy({
      top: scrollDifference,
      behavior: 'smooth',
    });
  }, []);


useEffect(() => {
  const targetId =
    getCommentId(replyTarget);

  if (!targetId) {
    return undefined;
  }

  const repositionTarget = () => {
    keepReplyTargetVisible(
      targetId
    );
  };

  const firstFrame =
    requestAnimationFrame(
      repositionTarget
    );

  const shortDelay =
    window.setTimeout(
      repositionTarget,
      120
    );

  const keyboardDelay =
    window.setTimeout(
      repositionTarget,
      320
    );

  const visualViewport =
    window.visualViewport;

  visualViewport?.addEventListener(
    'resize',
    repositionTarget
  );

  visualViewport?.addEventListener(
    'scroll',
    repositionTarget
  );

  return () => {
    cancelAnimationFrame(
      firstFrame
    );

    window.clearTimeout(
      shortDelay
    );

    window.clearTimeout(
      keyboardDelay
    );

    visualViewport?.removeEventListener(
      'resize',
      repositionTarget
    );

    visualViewport?.removeEventListener(
      'scroll',
      repositionTarget
    );
  };
}, [
  replyTarget,
  keepReplyTargetVisible,
]);


const [
  mainComposerState,
  setMainComposerState,
] = useState({
  textLength: 0,
  charactersRemaining: 1000,
  canSubmit: false,
  hasText: false,
  focused: false,
});

  const publishedLabel =
    formatPublishedDate(
      article?.publishedAt
    );

  const articleUrl =
    article?.canonicalUrl ||
    article?.sourceUrl ||
    '';

  const categories =
    useMemo(() => {
      if (
        !Array.isArray(
          article?.categories
        )
      ) {
        return [];
      }

      return article.categories
        .filter(Boolean)
        .slice(0, 4);
    }, [article?.categories]);

const discussionParticipants =
  useMemo(() => {
    const activity = [];

    comments.forEach((comment) => {
      const commentDeleted =
        comment?.isDeleted ||
        comment?.status === 'deleted';

      if (!commentDeleted) {
        activity.push(comment);
      }

      const replies =
        Array.isArray(comment?.replies)
          ? comment.replies
          : [];

      replies.forEach((reply) => {
        const replyDeleted =
          reply?.isDeleted ||
          reply?.status === 'deleted';

        if (!replyDeleted) {
          activity.push(reply);
        }
      });
    });

    activity.sort(
      (a, b) =>
        getDiscussionActivityTime(b) -
        getDiscussionActivityTime(a)
    );

    const seenParticipants =
      new Set();

    const uniqueParticipants = [];

    activity.forEach((item) => {
      const participantKey =
        getDiscussionParticipantKey(item);

      if (
        !participantKey ||
        seenParticipants.has(
          participantKey
        )
      ) {
        return;
      }

      seenParticipants.add(
        participantKey
      );

      uniqueParticipants.push(
        getDiscussionParticipant(item)
      );
    });

    return uniqueParticipants;
  }, [comments]);

const visibleDiscussionParticipants =
  discussionParticipants.slice(0, 6);

const remainingDiscussionParticipants =
  Math.max(
    0,
    discussionParticipants.length - 6
  );

const discussionParticipantCount =
  discussionParticipants.length;


  /* =========================================================
     ARTICLE REQUEST
  ========================================================= */

  useEffect(() => {
    let cancelled = false;

    const fetchArticle = async () => {
      if (!articleId) {
        setError(
          'This article is unavailable.'
        );

        setLoading(false);
        return;
      }

      try {
        if (!routeArticle) {
          setLoading(true);
        }

        setError('');

        const response =
          await api.get(
            `/news/${articleId}`
          );

        if (cancelled) {
          return;
        }

        const fetchedArticle =
          response.data?.article;

        if (!fetchedArticle) {
          throw new Error(
            'Article data was not returned.'
          );
        }

        setArticle(
          fetchedArticle
        );

        setImageFailed(false);
      } catch (requestError) {
        if (cancelled) {
          return;
        }

        console.error(
          '❌ Failed to load News article:',
          requestError
        );

        const message =
          requestError?.response?.data
            ?.message ||
          'This article could not be loaded right now.';

        setError(message);

        if (!routeArticle) {
          setArticle(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchArticle();

    return () => {
      cancelled = true;
    };
  }, [
    articleId,
    routeArticle,
  ]);

  /* =========================================================
     COMMENTS REQUEST
  ========================================================= */

  const fetchComments =
    useCallback(
      async ({
        page = 1,
        append = false,
      } = {}) => {
        if (!articleId) {
          return;
        }

        try {
          if (append) {
            setLoadingMoreComments(
              true
            );
          } else {
            setCommentsLoading(
              true
            );

            setCommentsError('');
          }

          const response =
            await api.get(
              `/news/${articleId}/comments`,
              {
                params: {
                  page,
                  limit:
                    COMMENT_PAGE_LIMIT,
                },
              }
            );

          const nextComments =
            Array.isArray(
              response.data?.comments
            )
              ? response.data.comments
              : [];

          const nextPagination =
            response.data?.pagination ||
            {
              page,
              limit:
                COMMENT_PAGE_LIMIT,
              total:
                nextComments.length,
              totalPages: 1,
              hasMore: false,
            };

          setComments(
            (previousComments) => {
              if (!append) {
                return nextComments;
              }

              const existingIds =
                new Set(
                  previousComments
                    .map(getCommentId)
                    .filter(Boolean)
                );

              const uniqueComments =
                nextComments.filter(
                  (comment) => {
                    const id =
                      getCommentId(
                        comment
                      );

                    return (
                      id &&
                      !existingIds.has(id)
                    );
                  }
                );


return [
  ...uniqueComments,
  ...previousComments,
];

            }
          );

          setCommentsPagination({
            page:
              Number(
                nextPagination.page
              ) || page,

            limit:
              Number(
                nextPagination.limit
              ) ||
              COMMENT_PAGE_LIMIT,

            total:
              Number(
                nextPagination.total
              ) || 0,

            totalPages:
              Number(
                nextPagination.totalPages
              ) || 1,

            hasMore:
              Boolean(
                nextPagination.hasMore
              ),
          });

          setCommentsError('');
        } catch (requestError) {
          console.error(
            '❌ Failed to load News comments:',
            requestError
          );

          const message =
            requestError?.response
              ?.data?.message ||
            'Discussions could not be loaded right now.';

          setCommentsError(message);

          if (!append) {
            setComments([]);
          }
        } finally {
          setCommentsLoading(false);
          setLoadingMoreComments(false);
        }
      },
      [articleId]
    );

  useEffect(() => {
    fetchComments({
      page: 1,
      append: false,
    });
  }, [fetchComments]);

  /* =========================================================
     CREATE TOP-LEVEL COMMENT
  ========================================================= */

  const handleCreateComment =
    async (text) => {
      if (
        !articleId ||
        commentSubmitting
      ) {
        return false;
      }

      try {
        setCommentSubmitting(true);
        setCommentSubmitError('');
        setDeleteError('');

        const response =
          await api.post(
            `/news/${articleId}/comments`,
            {
              text,
            }
          );

        const createdComment =
          response.data?.comment;

        if (!createdComment) {
          throw new Error(
            'The created comment was not returned.'
          );
        }

        const createdId =
          getCommentId(
            createdComment
          );

        setComments(
          (currentComments) => {
            const withoutDuplicate =
              currentComments.filter(
                (comment) =>
                  getCommentId(
                    comment
                  ) !== createdId
              );

return [
  ...withoutDuplicate,
  createdComment,
];  
        }
        );

        setCommentsPagination(
          (current) => ({
            ...current,
            total:
              Number(
                current.total || 0
              ) + 1,
          })
        );

        return true;
      } catch (requestError) {
        console.error(
          '❌ Failed to create News comment:',
          requestError
        );

        const message =
          requestError?.response
            ?.data?.message ||
          'Your comment could not be posted.';

        setCommentSubmitError(
          message
        );

        return false;
      } finally {
        setCommentSubmitting(false);
      }
    };

const handleSubmitComposer =
  async () => {
    const submitting =
      replyTarget
        ? Boolean(replySubmittingId)
        : commentSubmitting;

    if (
      submitting ||
      !mainComposerState.canSubmit
    ) {
      return;
    }

    await commentComposerRef
      .current
      ?.submit();
  };


  /* =========================================================
     CREATE REPLY
  ========================================================= */


const handleCreateReply =
  async ({
    comment,
    text,
  }) => {
    const targetCommentId =
      getCommentId(comment);

    if (
      !articleId ||
      !targetCommentId ||
      replySubmittingId
    ) {
      return false;
    }

    try {
      setReplySubmittingId(
        targetCommentId
      );

      setReplyErrors(
        (current) => ({
          ...current,
          [targetCommentId]: '',
        })
      );

      setDeleteError('');

      const response =
        await api.post(
          `/news/${articleId}/comments/${targetCommentId}/replies`,
          {
            text,
          }
        );

      const createdReply =
        response.data?.reply;

      if (!createdReply) {
        throw new Error(
          'The created reply was not returned.'
        );
      }

      const createdReplyId =
        getCommentId(
          createdReply
        );

      /*
       * parentCommentId = exact comment/reply being answered.
       *
       * rootCommentId = top-level discussion that owns
       * the flat replies array.
       */
      const parentCommentId =
        String(
          response.data?.parentCommentId ||
          createdReply?.parentCommentId ||
          targetCommentId
        );

      const rootCommentId =
        String(
          response.data?.rootCommentId ||
          createdReply?.rootCommentId ||
          comment?.rootCommentId ||
          targetCommentId
        );

      setComments(
        (currentComments) =>
          currentComments.map(
            (currentComment) => {
              const currentCommentId =
                getCommentId(
                  currentComment
                );

              /*
               * Always update the root discussion,
               * not necessarily the exact reply target.
               */
              if (
                currentCommentId !==
                rootCommentId
              ) {
                return currentComment;
              }

              const currentReplies =
                Array.isArray(
                  currentComment.replies
                )
                  ? currentComment.replies
                  : [];

              const repliesWithoutDuplicate =
                currentReplies.filter(
                  (reply) =>
                    getCommentId(
                      reply
                    ) !==
                    createdReplyId
                );

              return {
                ...currentComment,

                replyCount:
                  Number(
                    currentComment.replyCount ||
                    currentReplies.length ||
                    0
                  ) + 1,

                replies: [
                  ...repliesWithoutDuplicate,

                  {
                    ...createdReply,

                    parentCommentId:
                      createdReply.parentCommentId ||
                      parentCommentId,

                    rootCommentId:
                      createdReply.rootCommentId ||
                      rootCommentId,
                  },
                ],
              };
            }
          )
      );

      return true;
    } catch (requestError) {
      console.error(
        '❌ Failed to create News reply:',
        requestError
      );

      const message =
        requestError?.response
          ?.data?.message ||
        'Your reply could not be posted.';

      setReplyErrors(
        (current) => ({
          ...current,
          [targetCommentId]:
            message,
        })
      );

      return false;
    } finally {
      setReplySubmittingId('');
    }
  };



const handleStartReply =
  (target) => {
    if (
      !target ||
      target?.isDeleted ||
      target?.status === 'deleted'
    ) {
      return;
    }

    setReplyTarget(target);

    setCommentSubmitError('');

    const targetId =
      getCommentId(target);

    if (targetId) {
      setReplyErrors(
        (current) => ({
          ...current,
          [targetId]: '',
        })
      );
    }

    requestAnimationFrame(() => {
      commentComposerRef
        .current
        ?.focus();
    });
  };

const handleCancelReply =
  () => {
    setReplyTarget(null);

    commentComposerRef
      .current
      ?.clear();
  };

const handleSubmitComposerText =
  async (text) => {
    if (replyTarget) {
      const success =
        await handleCreateReply({
          comment:
            replyTarget,
          text,
        });

      if (success !== false) {
        setReplyTarget(null);
      }

      return success;
    }

    return handleCreateComment(
      text
    );
  };



  /* =========================================================
     REACT TO COMMENT OR REPLY
  ========================================================= */

const handleReactToComment =
  async (
    target,
    reactionType
  ) => {
    const targetId =
      getCommentId(target);

    if (
      !targetId ||
      !reactionType ||
      reactingId
    ) {
      return false;
    }

    if (
      target?.isDeleted ||
      target?.status === 'deleted'
    ) {
      return false;
    }

    try {
      setReactingId(
        targetId
      );

      const response =
        await api.put(
          `/news/comments/${targetId}/reaction`,
          {
            reactionType,
          }
        );

      const updatedCommentId =
        String(
          response.data?.commentId ||
          targetId
        );

      const reactionSummary =
        Array.isArray(
          response.data
            ?.reactionSummary
        )
          ? response.data
              .reactionSummary
          : [];

      const viewerReaction =
        response.data
          ?.viewerReaction ||
        null;

      setComments(
        (currentComments) =>
          updateNewsCommentReactionState(
            currentComments,
            updatedCommentId,
            {
              reactionSummary,
              viewerReaction,
            }
          )
      );

      return true;
    } catch (requestError) {
      console.error(
        '❌ Failed to react to News comment:',
        requestError
      );

      return false;
    } finally {
      setReactingId('');
    }
  };


  /* =========================================================
     DELETE COMMENT OR REPLY
  ========================================================= */

  const handleDeleteComment =
    async (target) => {
      const targetId =
        getCommentId(target);

      if (
        !targetId ||
        deletingId
      ) {
        return;
      }

      try {
        setDeletingId(targetId);
        setDeleteError('');

        const response =
          await api.delete(
            `/news/comments/${targetId}`
          );

        const deletedCommentId =
          String(
            response.data?.commentId ||
            targetId
          );

        const parentCommentId =
          response.data
            ?.parentCommentId
            ? String(
                response.data
                  .parentCommentId
              )
            : null;

        const rootCommentId =
          response.data
            ?.rootCommentId
            ? String(
                response.data
                  .rootCommentId
              )
            : null;

        const wasReply =
          response.data?.wasReply ===
            true ||
          Boolean(
            parentCommentId ||
            rootCommentId
          );

        setComments(
          (currentComments) =>
            currentComments.map(
              (comment) => {
                const commentId =
                  getCommentId(
                    comment
                  );

                if (
                  !wasReply &&
                  commentId ===
                    deletedCommentId
                ) {
                  return markItemDeleted(
                    comment
                  );
                }

                const parentId =
                  rootCommentId ||
                  parentCommentId;

                if (
                  wasReply &&
                  commentId ===
                    parentId
                ) {
                  const currentReplies =
                    Array.isArray(
                      comment.replies
                    )
                      ? comment.replies
                      : [];

                  return {
                    ...comment,

                    replyCount:
                      Math.max(
                        0,
                        Number(
                          comment.replyCount ||
                          currentReplies.length ||
                          0
                        ) - 1
                      ),

                    replies:
                      currentReplies.map(
                        (reply) =>
                          getCommentId(
                            reply
                          ) ===
                          deletedCommentId
                            ? markItemDeleted(
                                reply
                              )
                            : reply
                      ),
                  };
                }

                return comment;
              }
            )
        );
      } catch (requestError) {
        console.error(
          '❌ Failed to delete News comment:',
          requestError
        );

        const message =
          requestError?.response
            ?.data?.message ||
          'The comment could not be deleted.';

        setDeleteError(message);
      } finally {
        setDeletingId('');
      }
    };

  /* =========================================================
     PAGINATION / NAVIGATION
  ========================================================= */

  const handleLoadMoreComments =
    () => {
      if (
        commentsLoading ||
        loadingMoreComments ||
        !commentsPagination.hasMore
      ) {
        return;
      }

      fetchComments({
        page:
          commentsPagination.page +
          1,

        append: true,
      });
    };

  const handleRetryComments =
    () => {
      fetchComments({
        page: 1,
        append: false,
      });
    };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleOpenOfficialArticle =
    () => {
      if (!articleUrl) {
        return;
      }

      window.open(
        articleUrl,
        '_blank',
        'noopener,noreferrer'
      );
    };

  const hasHeroImage =
    Boolean(article?.imageUrl) &&
    !imageFailed;

  /* =========================================================
     COMMENT SKELETON
  ========================================================= */

  const renderCommentSkeletons =
    () => {
      return (
        <div
          className="news-detail-comments-skeleton"
          aria-label="Loading discussions"
        >
          {[1, 2, 3].map(
            (item) => (
              <div
                className="news-detail-comment-skeleton"
                key={`news-comment-skeleton-${item}`}
              >
                <div className="news-detail-comment-skeleton-avatar" />

                <div className="news-detail-comment-skeleton-body">
                  <div className="news-detail-comment-skeleton-name" />

                  <div className="news-detail-comment-skeleton-line" />

                  <div className="news-detail-comment-skeleton-line news-detail-comment-skeleton-line--short" />
                </div>
              </div>
            )
          )}
        </div>
      );
    };

  /* =========================================================
     ARTICLE LOADING STATE
  ========================================================= */

  if (
    loading &&
    !article
  ) {
    return (
      <div className="news-detail-screen">
        <header className="news-detail-header">
          <button
            type="button"
            className="news-detail-back-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img
              src={backIcon}
              alt=""
              className="news-detail-back-icon"
            />
          </button>

          <h1 className="news-detail-header-title">
            News
          </h1>

          <div
            className="news-detail-header-spacer"
            aria-hidden="true"
          />
        </header>

        <main className="news-detail-scroll-area">
          <div
            className="news-detail-skeleton"
            aria-label="Loading article"
          >
            <div className="news-detail-skeleton-image" />

            <div className="news-detail-skeleton-body">
              <div className="news-detail-skeleton-source" />
              <div className="news-detail-skeleton-meta" />
              <div className="news-detail-skeleton-title" />
              <div className="news-detail-skeleton-title news-detail-skeleton-title--short" />
              <div className="news-detail-skeleton-text" />
              <div className="news-detail-skeleton-text" />
              <div className="news-detail-skeleton-text news-detail-skeleton-text--short" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* =========================================================
     ARTICLE ERROR STATE
  ========================================================= */

  if (
    !article &&
    error
  ) {
    return (
      <div className="news-detail-screen">
        <header className="news-detail-header">
          <button
            type="button"
            className="news-detail-back-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img
              src={backIcon}
              alt=""
              className="news-detail-back-icon"
            />
          </button>

          <h1 className="news-detail-header-title">
            News
          </h1>

          <div
            className="news-detail-header-spacer"
            aria-hidden="true"
          />
        </header>

        <main className="news-detail-scroll-area">
          <div className="news-detail-error-state">
            <div className="news-detail-error-icon">
              !
            </div>

            <h2>
              Article unavailable
            </h2>

            <p>{error}</p>

            <div className="news-detail-error-actions">
              <button
                type="button"
                className="news-detail-error-btn"
                onClick={handleRetry}
              >
                Try again
              </button>

              <button
                type="button"
                className="news-detail-error-btn news-detail-error-btn--secondary"
                onClick={handleBack}
              >
                Go back
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* =========================================================
     MAIN SCREEN
  ========================================================= */

  return (
    <div className="news-detail-screen">
      <header className="news-detail-header">
        <button
          type="button"
          className="news-detail-back-btn"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img
            src={backIcon}
            alt=""
            className="news-detail-back-icon"
          />
        </button>

        <h1 className="news-detail-header-title">
          News
        </h1>

        <div
          className="news-detail-header-spacer"
          aria-hidden="true"
        />
      </header>

      <main className="news-detail-scroll-area">
        <article className="news-detail-article">
          <div className="news-detail-hero">
            {hasHeroImage ? (
              <img
                src={article.imageUrl}
                alt=""
                className="news-detail-hero-image"
                onError={() =>
                  setImageFailed(true)
                }
              />
            ) : (
              <div className="news-detail-hero-fallback">
                <img
                  src={piNetworkLogo}
                  alt=""
                  className="news-detail-hero-fallback-logo"
                />
              </div>
            )}

            <div className="news-detail-hero-overlay" />

            <div className="news-detail-source-badge">
              <span className="news-detail-source-logo-wrap">
                <img
                  src={piNetworkLogo}
                  alt=""
                  className="news-detail-source-logo"
                />
              </span>

              <span className="news-detail-source-name">
                {article.sourceName ||
                  'Pi Network'}
              </span>

              {article.isVerifiedSource && (
                <img
                  src={newsGoldTickIcon}
                  alt="Verified official source"
                  title="Verified official source"
                  className="news-detail-verified-icon"
                />
              )}
            </div>
          </div>

          <div className="news-detail-article-body">
            <div className="news-detail-meta-row">
              {publishedLabel && (
                <span className="news-detail-published-date">
                  {publishedLabel}
                </span>
              )}

              {publishedLabel &&
                categories.length >
                  0 && (
                  <span
                    className="news-detail-meta-dot"
                    aria-hidden="true"
                  />
                )}

              {categories.length >
                0 && (
                <div className="news-detail-categories">
                  {categories.map(
                    (category) => (
                      <span
                        className="news-detail-category"
                        key={`${article._id}-${category}`}
                      >
                        {category}
                      </span>
                    )
                  )}
                </div>
              )}
            </div>

            <h2 className="news-detail-title">
              {article.title ||
                'Pi Network update'}
            </h2>

            {article.authorName && (
              <p className="news-detail-author">
                By {article.authorName}
              </p>
            )}


<p className="news-detail-excerpt">
  {article.excerpt ||
    'Read the complete official Pi Network update and join the discussion below.'}
</p>

{articleUrl && (
  <button
    type="button"
    className="news-detail-official-inline-btn"
    onClick={
      handleOpenOfficialArticle
    }
    aria-label="Read the complete article on the official source"
  >
    <span>
      Read official article
    </span>

    <span
      className="news-detail-official-inline-arrow"
      aria-hidden="true"
    >
      ↗
    </span>
  </button>
)}

{error && (
  <div className="news-detail-inline-warning">


                <span>
                  The latest article data could not be refreshed.
                </span>

                <button
                  type="button"
                  onClick={handleRetry}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </article>

        <section className="news-detail-discussion">



<div className="news-detail-discussion-heading">
  <div className="news-detail-discussion-heading-top">
    <div className="news-detail-discussion-heading-copy">
      <span className="news-detail-section-label">
        Pioneer Discussion
      </span>

      <h3>
        Join the conversation
      </h3>
    </div>

    {!commentsLoading && (
      <span className="news-detail-comment-count">
        <img
          src={discussIcon}
          alt=""
          aria-hidden="true"
          className="news-detail-comment-count-icon"
        />

        <span>
          {commentsPagination.total}{' '}
          {commentsPagination.total === 1
            ? 'comment'
            : 'comments'}
        </span>
      </span>
    )}
  </div>

  <p className="news-detail-discussion-description">
    Discuss this Pi Network announcement with other pioneers.
  </p>

  {!commentsLoading &&
    visibleDiscussionParticipants.length > 0 && (
      <div className="news-detail-discussion-presence">
        <div
          className="news-detail-participant-stack"
          aria-label={`${discussionParticipantCount} pioneers participating`}
        >
          {visibleDiscussionParticipants.map(
            (participant, index) => (
              <img
                key={
                  participant.id ||
                  participant.username ||
                  `discussion-participant-${index}`
                }
                src={
                  participant.profilePic ||
                  placeholderProfile
                }
                alt=""
                title={
                  participant.name ||
                  participant.username
                }
                className="news-detail-participant-avatar"
                onError={(event) => {
                  event.currentTarget.src =
                    placeholderProfile;
                }}
              />
            )
          )}

          {remainingDiscussionParticipants > 0 && (
            <span className="news-detail-participant-more">
              +{remainingDiscussionParticipants}
            </span>
          )}
        </div>

        <span className="news-detail-participant-label">
          {discussionParticipantCount}{' '}
          {discussionParticipantCount === 1
            ? 'pioneer'
            : 'pioneers'}{' '}
          discussing
        </span>
      </div>
    )}
</div>


          {deleteError && (
            <div className="news-detail-comments-inline-error">
              <span>
                {deleteError}
              </span>

              <button
                type="button"
                onClick={() =>
                  setDeleteError('')
                }
              >
                Dismiss
              </button>
            </div>
          )}



{commentsPagination.hasMore &&
comments.length > 0 ? (
  <button
    type="button"
    className="news-detail-load-more-comments"
    onClick={
      handleLoadMoreComments
    }
    disabled={
      loadingMoreComments
    }
  >
    {loadingMoreComments
      ? 'Loading older comments…'
      : 'Load older comments'}
  </button>
) : null}


          {commentsLoading &&
          comments.length === 0 ? (
            renderCommentSkeletons()
          ) : null}




          {commentsError &&
          comments.length === 0 ? (
            <div className="news-detail-comments-state">
              <div className="news-detail-comments-state-icon">
                !
              </div>

              <h4>
                Discussions unavailable
              </h4>

              <p>
                {commentsError}
              </p>

              <button
                type="button"
                onClick={
                  handleRetryComments
                }
              >
                Try again
              </button>
            </div>
          ) : null}

          {!commentsLoading &&
          !commentsError &&
          comments.length === 0 ? (
            <div className="news-detail-comments-state">


<div className="news-detail-comments-state-icon news-detail-comments-state-icon--empty">
  <img
    src={discussIcon}
    alt=""
    aria-hidden="true"
    className="news-detail-comments-empty-icon"
  />
</div>
              <h4>
                Start the conversation
              </h4>

              <p>
                Be the first pioneer to share what you think about this update.
              </p>
            </div>
          ) : null}

          {comments.length > 0 && (
            <div className="news-detail-comments-list">
              {comments.map(
                (comment) => {
                  const commentId =
                    getCommentId(
                      comment
                    );

                  return (


<NewsCommentItem
  key={commentId}
  comment={comment}
  user={user}
  replyTarget={replyTarget}
  onStartReply={handleStartReply}
  onDelete={handleDeleteComment}
  onReact={handleReactToComment}
  deletingId={deletingId}
  reactingId={reactingId}
  replySubmittingId={replySubmittingId}
  replyErrors={replyErrors}
/>


                  );
                }
              )}
            </div>
          )}

          {commentsError &&
          comments.length > 0 ? (
            <div className="news-detail-comments-inline-error">
              <span>
                {commentsError}
              </span>

              <button
                type="button"
                onClick={
                  handleLoadMoreComments
                }
              >
                Retry
              </button>
            </div>
          ) : null}








        </section>
      </main>



<footer className="news-detail-comment-footer">
  {replyTarget && (
    <div className="news-detail-replying-bar">
      <span className="news-detail-replying-text">
        Replying to{' '}
        <strong>
          @
          {replyTarget?.author?.username ||
            replyTarget?.username ||
            'user'}
        </strong>
      </span>

      <button
        type="button"
        className="news-detail-replying-cancel"
        onClick={handleCancelReply}
        aria-label="Cancel reply"
      >
        ×
      </button>
    </div>
  )}

  <div className="news-detail-comment-pill">
    <img
      src={
        user?.profilePic ||
        placeholderProfile
      }
      alt=""
      className="news-detail-comment-pill-avatar"
      onError={(event) => {
        event.currentTarget.src =
          placeholderProfile;
      }}
    />

    <div className="news-detail-comment-pill-composer">
      <NewsCommentComposer
        ref={commentComposerRef}
        user={user}
        mode={
          replyTarget
            ? 'reply'
            : 'comment'
        }
        replyingTo={
          replyTarget
            ? {
                commentId:
                  getCommentId(
                    replyTarget
                  ),

                username:
                  replyTarget?.author
                    ?.username ||
                  replyTarget
                    ?.username ||
                  '',
              }
            : null
        }
        submitting={
          replyTarget
            ? Boolean(
                replySubmittingId
              )
            : commentSubmitting
        }
        error={
          replyTarget
            ? replyErrors[
                getCommentId(
                  replyTarget
                )
              ] || ''
            : commentSubmitError
        }
        useExternalActions
        onSubmit={
          handleSubmitComposerText
        }
        onStateChange={
          setMainComposerState
        }
      />
    </div>

    <button
      type="button"
      className="news-detail-comment-send-btn"
      onClick={
        handleSubmitComposer
      }
      disabled={
        !mainComposerState.canSubmit ||
        commentSubmitting ||
        Boolean(
          replySubmittingId
        )
      }
      aria-label={
        replyTarget
          ? replySubmittingId
            ? 'Posting reply'
            : 'Post reply'
          : commentSubmitting
          ? 'Posting comment'
          : 'Post comment'
      }
    >
      {commentSubmitting ||
      replySubmittingId ? (
        <span
          className="news-detail-comment-send-spinner"
          aria-hidden="true"
        />
      ) : (
        <img
          src={sendIcon}
          alt=""
          className="news-detail-comment-send-icon"
        />
      )}
    </button>
  </div>
</footer>



    </div>
  );
}

export default NewsDetailScreen;
